import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { normalizeCurrencySymbol } from '../types/store';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  formatPrice: (amount: number | string | undefined) => string;
  setCurrency: (currency: string, symbol: string) => void;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: 'MYR',
  currencySymbol: 'RM',
  formatPrice: (amount) => `RM${amount || 0}`,
  setCurrency: () => {},
});

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: React.ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  // Get currency from Redux store (synced with active store)
  const activeStoreCurrency = useSelector((state: RootState) => state.auth.activeStoreCurrency);
  const activeStoreCurrencySymbol = useSelector((state: RootState) => state.auth.activeStoreCurrencySymbol);
  
  // Local state that syncs with Redux and localStorage
  const [currency, setCurrencyState] = useState<string>(() => {
    return localStorage.getItem('store-currency') || 'MYR';
  });
  const [currencySymbol, setCurrencySymbol] = useState<string>(() => {
    return normalizeCurrencySymbol(
      localStorage.getItem('store-currency') || 'MYR',
      localStorage.getItem('store-currency-symbol') || 'RM',
    );
  });

  // Sync with Redux state when active store changes
  useEffect(() => {
    if (activeStoreCurrency && activeStoreCurrencySymbol) {
      setCurrencyState(activeStoreCurrency);
      setCurrencySymbol(normalizeCurrencySymbol(activeStoreCurrency, activeStoreCurrencySymbol));
    }
  }, [activeStoreCurrency, activeStoreCurrencySymbol]);

  // Update localStorage when currency changes
  const setCurrency = (newCurrency: string, newSymbol: string) => {
    const normalizedSymbol = normalizeCurrencySymbol(newCurrency, newSymbol);
    setCurrencyState(newCurrency);
    setCurrencySymbol(normalizedSymbol);
    localStorage.setItem('store-currency', newCurrency);
    localStorage.setItem('store-currency-symbol', normalizedSymbol);
  };

  const value = useMemo(() => {
    const formatPrice = (amount: number | string | undefined): string => {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
      return `${currencySymbol} ${numAmount.toFixed(2)}`;
    };

    return {
      currency,
      currencySymbol,
      formatPrice,
      setCurrency,
    };
  }, [currency, currencySymbol]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
